import React, { useCallback, useEffect, useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import CompetitorFormModal from "./CompetitorFormModal";
import CompetitorSuggestionsModal from "./CompetitorSuggestionsModal";
import { adaptCompetitors } from "./monitoringAdapters";
import { ProviderIcon } from "../shared/ProviderIcon";
import {
	addMonitoringCompetitor,
	getMonitoringCompetitors,
	ignoreMonitoringCompetitor,
	suggestMonitoringCompetitors,
	trackMonitoringCompetitor,
	updateMonitoringCompetitor,
} from "../../../services/monitoringApi";

const URL_REQUIRED_MSG = "A website URL is required to track this competitor";

function stripUrlToHost(value) {
	return String(value || "")
		.trim()
		.replace(/^https?:\/\//i, "")
		.replace(/^www\./i, "")
		.replace(/\/.*$/, "")
		.trim();
}

function looksLikeHost(value) {
	const v = String(value || "").trim();
	if (!v || /\s/.test(v)) return false;
	return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(
		stripUrlToHost(v)
	);
}

/** Ensure suggestions always yield both name + domain when either side can be inferred. */
function normalizeSuggestion(s = {}) {
	let domain = stripUrlToHost(s.domain || s.url || s.website || "");
	let name = String(s.name || "").trim();

	if (!domain && name && looksLikeHost(name)) {
		domain = stripUrlToHost(name);
		name = "";
	}
	if (!name && domain) {
		const label = domain.split(".")[0] || domain;
		name = label.charAt(0).toUpperCase() + label.slice(1);
	}
	if (!name && domain) {
		name = domain;
	}

	return { name, domain };
}

function winLossLabel(cell) {
	if (!cell || cell.status === "empty") return "—";
	if (cell.status === "lead") return `We lead ${cell.us}–${cell.them}`;
	if (cell.status === "behind") return `They lead ${cell.them}–${cell.us}`;
	return `Tied ${cell.us}–${cell.them}`;
}

const WIN_LOSS_FILTERS = [
	{ value: "all", label: "Show all" },
	{ value: "lead", label: "We lead" },
	{ value: "tied", label: "Tied" },
	{ value: "behind", label: "They lead" },
];

function isWinLossFilterMatch(cell, filter) {
	if (filter === "all") return true;
	if (!cell || cell.status === "empty") return false;
	return cell.status === filter;
}

/** Match reference SoV pie: you = brand primary, rest = slate, others = categorical ramp. */
const SOV_YOU_COLOR = "#2563EB";
const SOV_REST_COLOR = "#6B7280";
const SOV_PIE_RAMP = [
	"#7C3AED",
	"#0E8F6E",
	"#E4682B",
	"#2563EB",
	"#DB2777",
	"#0891B2",
	"#CA8A04",
	"#65A30D",
	"#BE185D",
	"#4338CA",
];

function isYouSliceKey(key) {
	const k = String(key || "").toLowerCase();
	return k === "you" || k === "us" || k === "ours" || k === "brand";
}

function isRestSliceKey(key) {
	const k = String(key || "").toLowerCase();
	return k === "rest" || k === "others" || k === "other";
}

function formatSov1(value) {
	if (value == null || Number.isNaN(Number(value))) return "—";
	return Number(value).toFixed(1);
}

function MarketCompetitionCaption({ mc }) {
	if (!mc) return null;
	const measuredOn = mc.scopeKey === "all" ? mc.monitoredPrompts : mc.unbrandedPrompts;
	const monitored = mc.monitoredPrompts || measuredOn;

	if (mc.scopeKey === "all") {
		return (
			<p className="monitoring__panel-caption mb-3">
				Measured on <strong>all prompts</strong>
				{monitored ? ` — ${monitored} monitored` : ""}. Includes branded queries
				(which often name your brand and can inflate SoV). Change in{" "}
				<strong>Settings → Share of Voice scope</strong>. Share of prompts where each
				brand appears (named or cited), so heavily-cited players (e.g. retailers) count
				too.
			</p>
		);
	}

	return (
		<p className="monitoring__panel-caption mb-3">
			Measured on <strong>unbranded (competitive) prompts</strong>
			{measuredOn || monitored
				? ` — ${measuredOn} of ${monitored || measuredOn} monitored`
				: ""}
			. Branded prompts are excluded (they trivially name your brand and inflate SoV).
			Change in <strong>Settings → Share of Voice scope</strong>. Share of prompts where
			each brand appears (named or cited), so heavily-cited players (e.g. retailers) count
			too.
		</p>
	);
}

function sliceColor(slice, index) {
	const key = slice.key || slice.name;
	if (isYouSliceKey(key)) return SOV_YOU_COLOR;
	if (isRestSliceKey(key)) return SOV_REST_COLOR;
	return SOV_PIE_RAMP[index % SOV_PIE_RAMP.length];
}

/** Interactive SoV donut (You + competitors + Rest) — matches frontend PieChart. */
function SovPieChart({ slices, activeIndex, onActiveChange }) {
	const shown = (slices || []).filter((s) => Number(s.value) > 0);
	if (!shown.length) {
		return (
			<p className="monitoring__empty text-muted mb-0 py-4 text-center">
				No mentions measured yet.
			</p>
		);
	}

	const size = 200;
	const cx = 100;
	const cy = 100;
	const r = 86;
	const hole = 48;
	let acc = -Math.PI / 2;
	const arcs = shown.map((s, i) => {
		const a0 = acc;
		const a1 = acc + (Number(s.value) / 100) * 2 * Math.PI;
		acc = a1;
		const large = a1 - a0 > Math.PI ? 1 : 0;
		const pop = activeIndex === i ? 5 : 0;
		const mid = (a0 + a1) / 2;
		const ox = Math.cos(mid) * pop;
		const oy = Math.sin(mid) * pop;
		const pt = (ang, rad) =>
			`${(cx + ox + Math.cos(ang) * rad).toFixed(2)},${(cy + oy + Math.sin(ang) * rad).toFixed(2)}`;
		const d = `M ${pt(a0, r)} A ${r} ${r} 0 ${large} 1 ${pt(a1, r)} L ${pt(a1, hole)} A ${hole} ${hole} 0 ${large} 0 ${pt(a0, hole)} Z`;
		return { d, color: s.color, slice: s, i };
	});

	const youSlice = shown.find((s) => isYouSliceKey(s.key));
	const active = activeIndex != null ? shown[activeIndex] : null;
	const centerPct = active ? active.value : youSlice?.value ?? 0;
	const centerLabel = active ? active.name : "Your presence";

	return (
		<div className="monitoring__sov-layout">
			<div className="monitoring__sov-donut-wrap monitoring__sov-donut-wrap--svg">
				<svg
					width={size}
					height={size}
					viewBox={`0 0 ${size} ${size}`}
					role="img"
					aria-label="Share of voice composition"
				>
					{arcs.map((a) => (
						<path
							key={a.slice.key || a.slice.name}
							d={a.d}
							fill={a.color}
							opacity={activeIndex == null || activeIndex === a.i ? 1 : 0.55}
							stroke="#fff"
							strokeWidth="1.5"
							style={{ cursor: "pointer", transition: "opacity .12s" }}
							onMouseEnter={() => onActiveChange(a.i)}
							onMouseLeave={() => onActiveChange(null)}
						/>
					))}
				</svg>
				<div className="monitoring__sov-donut-center">
					<span className="monitoring__sov-donut-pct">{centerPct}%</span>
					<span className="monitoring__sov-donut-label" title={centerLabel}>
						{centerLabel}
					</span>
				</div>
			</div>
			<ul className="monitoring__sov-legend list-unstyled mb-0">
				{shown.map((entry, i) => (
					<li
						key={entry.key || entry.name}
						className={`monitoring__sov-legend-item${
							isYouSliceKey(entry.key) ? " monitoring__sov-legend-item--you" : ""
						}${isRestSliceKey(entry.key) ? " monitoring__sov-legend-item--rest" : ""}`}
						style={{
							opacity: activeIndex == null || activeIndex === i ? 1 : 0.55,
						}}
						onMouseEnter={() => onActiveChange(i)}
						onMouseLeave={() => onActiveChange(null)}
					>
						<span
							className="monitoring__sov-legend-swatch"
							style={{ background: entry.color }}
							aria-hidden="true"
						/>
						<span className="monitoring__sov-legend-name" title={entry.name}>
							{entry.name}
						</span>
						<span className="monitoring__sov-legend-pct">{entry.value}%</span>
						<span className="monitoring__sov-legend-mentions">
							{entry.mentions} mentions
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

function ProviderSovRing({ value }) {
	const clamped = Math.max(0, Math.min(100, Number(value) || 0));
	const ringData = [
		{ name: "sov", value: clamped },
		{ name: "rest", value: Math.max(0, 100 - clamped) },
	];
	return (
		<div className="monitoring__provider-ring" aria-hidden="true">
			<ResponsiveContainer width="100%" height="100%">
				<PieChart>
					<Pie
						data={ringData}
						dataKey="value"
						cx="50%"
						cy="50%"
						innerRadius={18}
						outerRadius={26}
						startAngle={90}
						endAngle={-270}
						stroke="none"
					>
						<Cell fill="#1E3A5F" />
						<Cell fill="#E5E7EB" />
					</Pie>
				</PieChart>
			</ResponsiveContainer>
		</div>
	);
}

function CompName({ name, domain }) {
	return (
		<span className="monitoring__comp-name">
			{name || domain}
			{domain && name !== domain ? (
				<span className="text-muted"> ({domain})</span>
			) : null}
		</span>
	);
}

export default function MonitoringCompetitors({ websiteId }) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [addOpen, setAddOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [trackAfterEdit, setTrackAfterEdit] = useState(false);
	const [suggestOpen, setSuggestOpen] = useState(false);
	const [suggests, setSuggests] = useState([]);
	const [suggestLoading, setSuggestLoading] = useState(false);
	const [trackBusyKey, setTrackBusyKey] = useState(null);
	const [busy, setBusy] = useState(false);
	const [activePieIndex, setActivePieIndex] = useState(null);
	const [winLossFilter, setWinLossFilter] = useState("all");

	const load = useCallback(async () => {
		if (!websiteId) {
			setLoading(false);
			setError("Missing website");
			return;
		}
		setLoading(true);
		setError(null);
		const res = await getMonitoringCompetitors(websiteId);
		if (!res.ok) {
			setError(res.message || "Failed to load competitors");
			setData(null);
			setLoading(false);
			return;
		}
		setData(adaptCompetitors(res.competitors));
		setLoading(false);
	}, [websiteId]);

	useEffect(() => {
		load();
	}, [load]);

	async function add(name, domain, { fromSuggestion = false } = {}) {
		const n = (name || "").trim();
		const d = (domain || "").trim();
		if (!n || !d) {
			if (fromSuggestion) {
				toast.error(URL_REQUIRED_MSG);
			}
			return false;
		}
		setBusy(true);
		const res = await addMonitoringCompetitor(websiteId, {
			name: n,
			domain: d,
		});
		setBusy(false);
		if (!res.ok) {
			toast.error(res.message || "Could not add competitor");
			return false;
		}
		setAddOpen(false);
		if (fromSuggestion) {
			const dNorm = stripUrlToHost(d);
			setSuggests((list) =>
				(list || []).map((x) => {
					const xn = normalizeSuggestion(x);
					const sameDomain = xn.domain && xn.domain === dNorm;
					const sameName =
						(x.name && String(x.name).trim() === n) || xn.name === n;
					return sameDomain || sameName ? { ...x, tracked: true } : x;
				})
			);
		}
		toast.success("Competitor added");
		load();
		return true;
	}

	async function trackFromSuggestion(suggestion) {
		const { name: n, domain: d } = normalizeSuggestion(suggestion);
		if (!d) {
			toast.error(URL_REQUIRED_MSG);
			return;
		}
		if (!n) {
			toast.error("A competitor name is required to track this competitor");
			return;
		}
		const key =
			suggestion?.domain ||
			suggestion?.url ||
			suggestion?.website ||
			suggestion?.name ||
			d ||
			n;
		setTrackBusyKey(key);
		await add(n, d, { fromSuggestion: true });
		setTrackBusyKey(null);
	}

	async function handleAddFromModal({ name, domain }) {
		await add(name, domain);
	}

	async function handleEditFromModal({ name, domain }) {
		if (!editing?.competitor_id) return;
		const n = (name || "").trim();
		const d = stripUrlToHost(domain);
		if (!n || !d) {
			toast.error(URL_REQUIRED_MSG);
			return;
		}
		setBusy(true);
		const res = await updateMonitoringCompetitor(websiteId, {
			competitorId: editing.competitor_id,
			name: n,
			domain: d,
		});
		if (!res.ok) {
			setBusy(false);
			toast.error(res.message || "Could not update competitor");
			return;
		}
		if (trackAfterEdit) {
			const trackRes = await trackMonitoringCompetitor(
				websiteId,
				editing.competitor_id
			);
			setBusy(false);
			setEditing(null);
			setTrackAfterEdit(false);
			if (!trackRes.ok) {
				toast.error(trackRes.message || "Could not re-add competitor");
				load();
				return;
			}
			toast.success("Competitor re-added");
			load();
			return;
		}
		setBusy(false);
		setEditing(null);
		toast.success("Competitor updated");
		load();
	}

	function resolveCompetitorNameDomain(competitor) {
		let name = String(competitor?.name || "").trim();
		let domain = stripUrlToHost(
			competitor?.domain || competitor?.url || competitor?.website || ""
		);
		// URL stored in name with empty domain → treat as domain-only (name missing)
		if (!domain && name && looksLikeHost(name)) {
			domain = stripUrlToHost(name);
			name = "";
		}
		return { name, domain };
	}

	async function track(competitor) {
		if (!competitor?.competitor_id) return;
		const { name, domain } = resolveCompetitorNameDomain(competitor);

		if (!name || !domain) {
			setTrackAfterEdit(true);
			setEditing({
				...competitor,
				name,
				domain,
			});
			toast.info(
				!domain
					? "Add a website URL to track this competitor"
					: "Add a competitor name to track this competitor"
			);
			return;
		}

		setBusy(true);
		const res = await trackMonitoringCompetitor(
			websiteId,
			competitor.competitor_id
		);
		setBusy(false);
		if (!res.ok) {
			toast.error(res.message || "Could not update competitor");
			return;
		}
		toast.success("Competitor tracked");
		load();
	}

	async function ignore(competitorId) {
		const res = await ignoreMonitoringCompetitor(websiteId, competitorId);
		if (!res.ok) {
			toast.error(res.message || "Could not update competitor");
			return;
		}
		load();
	}

	async function suggest() {
		setSuggestOpen(true);
		setSuggestLoading(true);
		setSuggests([]);
		const res = await suggestMonitoringCompetitors(websiteId);
		setSuggestLoading(false);
		if (!res.ok) {
			toast.error(res.message || "Could not fetch suggestions");
			return;
		}
		setSuggests(res.suggestions || []);
		if (!res.suggestions?.length) {
			toast.info(
				`No vendor suggestions available${res.provider ? ` (provider: ${res.provider})` : ""}.`
			);
		}
	}

	if (loading) {
		return <p className="monitoring__empty text-muted mb-0">Loading competitors…</p>;
	}
	if (error) {
		return <p className="monitoring__empty monitoring__empty--error mb-0">{error}</p>;
	}
	if (!data) return null;

	const matrix = data.winLossMatrix || {};
	const matrixCompetitors = matrix.competitors || [];
	const matrixVisibleRows = (matrix.rows || []).filter((row) => {
		if (winLossFilter === "all") return true;
		return matrixCompetitors.some((comp) =>
			isWinLossFilterMatch(matrix.cells?.[row.cluster]?.[comp], winLossFilter)
		);
	});
	const mc = data.marketCompetition;
	const comps = data.competitorsList || [];
	const active = comps.filter((c) => c.status === "active");
	const suggested = comps.filter((c) => c.status === "suggested");
	const ignored = comps.filter((c) => c.status === "ignored");
	const sources = data.citationSources || [];
	const pieData = (data.pie?.slices || []).map((s, i) => ({
		key: s.key,
		name: s.label,
		value: s.share,
		mentions: s.mentions ?? 0,
		color: sliceColor(s, i),
	}));
	const hasPie = pieData.some((s) => Number(s.value) > 0);

	return (
		<div className="monitoring__tab-content monitoring__competitors">
			<div className="monitoring__filter-bar">
				<Button
					color="primary"
					size="sm"
					className="monitoring__action-btn monitoring__action-btn--primary"
					disabled={busy}
					onClick={() => {
						setEditing(null);
						setTrackAfterEdit(false);
						setAddOpen(true);
					}}
				>
					Add competitor
				</Button>
				<Button
					size="sm"
					className="monitoring__action-btn"
					disabled={busy || suggestLoading || Boolean(trackBusyKey)}
					onClick={suggest}
				>
					Suggest from search data
				</Button>
			</div>

			<CompetitorFormModal
				isOpen={addOpen}
				onClose={() => setAddOpen(false)}
				onSubmit={handleAddFromModal}
				busy={busy}
				title="Add competitor"
				submitLabel="Add competitor"
				busyLabel="Adding…"
			/>

			<CompetitorFormModal
				isOpen={Boolean(editing)}
				onClose={() => {
					setEditing(null);
					setTrackAfterEdit(false);
				}}
				onSubmit={handleEditFromModal}
				busy={busy}
				title={
					trackAfterEdit
						? "Complete competitor details to re-add"
						: "Edit competitor"
				}
				submitLabel={trackAfterEdit ? "Save & re-add" : "Save"}
				busyLabel={trackAfterEdit ? "Re-adding…" : "Saving…"}
				initialName={editing?.name || ""}
				initialDomain={editing?.domain || ""}
			/>

			<CompetitorSuggestionsModal
				isOpen={suggestOpen}
				onClose={() => {
					if (trackBusyKey) return;
					setSuggestOpen(false);
				}}
				loading={suggestLoading}
				suggestions={suggests}
				busyKey={trackBusyKey}
				onTrack={trackFromSuggestion}
			/>

			{!mc ? (
				<article className="monitoring__panel monitoring__empty-panel mb-3">
					<p className="monitoring__empty text-muted mb-0">
						No competitive signal yet. Once a prompt run completes, Share of Voice
						is measured from the brands AI names in its answers — the same method
						and numbers as your audit&apos;s Market Competition Analysis.
						Competitors are auto-seeded from your audit; add or curate above.
					</p>
				</article>
			) : (
				<article className="monitoring__panel mb-3">
					<div className="monitoring__panel-header">
						<div className="monitoring__panel-header-left d-flex align-items-center gap-2 flex-wrap">
							<h3 className="monitoring__panel-title mb-0">
								Market Competition Analysis
							</h3>
							{mc.isMeasured && (
								<span className="monitoring__measured-badge">Measured</span>
							)}
							<span className="monitoring__scope-badge">
								{mc.scopeLabel || "Unbranded"}
							</span>
						</div>
					</div>
					<MarketCompetitionCaption mc={mc} />

					{hasPie && (
						<div className="mb-3">
							<SovPieChart
								slices={pieData}
								activeIndex={activePieIndex}
								onActiveChange={setActivePieIndex}
							/>
						</div>
					)}

					{mc.perProvider?.length > 0 && (
						<div className="monitoring__provider-grid">
							{mc.perProvider.map((p) => (
								<div key={p.engine} className="monitoring__provider-card">
									<div className="monitoring__provider-card-top">
										<div className="monitoring__provider-label">
											<ProviderIcon provider={p.engine} size={16} />
											{p.label}
										</div>
										<ProviderSovRing value={p.sov} />
									</div>
									<div className="monitoring__metric-value">
										{formatSov1(p.sov)}
										<span className="monitoring__metric-unit">/100</span>
									</div>
									<p className="monitoring__metric-hint mb-0">
										{p.brandMentions} brand vs {p.competitorMentions} competitor
									</p>
								</div>
							))}
						</div>
					)}

					<p className="monitoring__market-summary mt-3 mb-0">
						Measured share of voice{" "}
						<strong>{formatSov1(mc.overallSov)}/100</strong> — {mc.brandMentions}{" "}
						brand vs {mc.competitorMentions} competitor mentions across{" "}
						{mc.totalPrompts} prompts. Prompt outcomes:{" "}
						<strong>
							{mc.winningPrompts} won · {mc.losingPrompts} lost.
						</strong>
					</p>
				</article>
			)}

			{(matrix.rows || []).length > 0 && (
				<article className="monitoring__panel monitoring__panel--matrix mb-3">
					<div className="monitoring__matrix-header">
						<h3 className="monitoring__panel-title mb-0">
							Win / loss matrix — prompt clusters × competitors
						</h3>
						<div
							className="monitoring__matrix-filters"
							role="tablist"
							aria-label="Win / loss filter"
						>
							{WIN_LOSS_FILTERS.map((f) => (
								<button
									key={f.value}
									type="button"
									role="tab"
									aria-selected={winLossFilter === f.value}
									className={`monitoring__matrix-filter${
										winLossFilter === f.value
											? " monitoring__matrix-filter--active"
											: ""
									}`}
									onClick={() => setWinLossFilter(f.value)}
								>
									{f.label}
								</button>
							))}
						</div>
					</div>
					<div className="monitoring__table-scroll monitoring__table-scroll--matrix">
						<table className="monitoring__table monitoring__matrix-table">
							<thead>
								<tr>
									<th scope="col">Topic</th>
									{matrixCompetitors.map((comp) => (
										<th key={comp} scope="col" title={comp}>
											{String(comp || "").toUpperCase()}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{!matrixVisibleRows.length ? (
									<tr>
										<td
											colSpan={1 + matrixCompetitors.length}
											className="text-muted text-center py-4"
										>
											No topics match this filter.
										</td>
									</tr>
								) : (
									matrixVisibleRows.map((row) => (
										<tr key={row.cluster}>
											<td className="monitoring__td-prompt">
												<div className="monitoring__matrix-topic">
													{row.cluster}
												</div>
												<div className="monitoring__matrix-topic-meta text-muted">
													{row.prompts} prompt
													{row.prompts === 1 ? "" : "s"}
												</div>
											</td>
											{matrixCompetitors.map((comp) => {
												const cell =
													matrix.cells?.[row.cluster]?.[comp];
												const status = cell?.status || "empty";
												const matches = isWinLossFilterMatch(
													cell,
													winLossFilter
												);
												const dimmed =
													winLossFilter !== "all" && !matches;
												return (
													<td key={`${row.cluster}-${comp}`}>
														<span
															className={`monitoring__wl-badge monitoring__wl-badge--${status}${
																dimmed
																	? " monitoring__wl-badge--dimmed"
																	: ""
															}`}
														>
															{winLossLabel(cell)}
														</span>
													</td>
												);
											})}
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
					{matrix.footnote && (
						<p className="monitoring__table-footnote mb-0">{matrix.footnote}</p>
					)}
				</article>
			)}

			<div className="monitoring__detail-grid monitoring__detail-grid--competitors">
				<article className="monitoring__panel">
					<h3 className="monitoring__panel-title">
						Tracked competitors ({active.length})
					</h3>
					<ul className="monitoring__comp-list list-unstyled mb-0">
						{active.map((c) => (
							<li key={c.competitor_id} className="monitoring__comp-row">
								<CompName name={c.name} domain={c.domain} />
								<span className="monitoring__comp-meta text-muted">
									{c.mentions_last ?? 0} mentions
								</span>
								<span className="ms-auto d-flex gap-1">
									<button
										type="button"
										className="monitoring__trend-toggle"
										onClick={() => {
											setTrackAfterEdit(false);
											setEditing(c);
										}}
									>
										Edit
									</button>
									<button
										type="button"
										className="monitoring__trend-toggle monitoring__trend-toggle--danger"
										onClick={() => ignore(c.competitor_id)}
									>
										Ignore
									</button>
								</span>
							</li>
						))}
						{active.length === 0 && (
							<li className="text-muted">None tracked yet.</li>
						)}
					</ul>
					{suggested.length > 0 && (
						<div className="mt-3">
							<p className="monitoring__panel-caption mb-2">Suggested (AI-named)</p>
							<ul className="monitoring__comp-list list-unstyled mb-0">
								{suggested.map((c) => (
									<li key={c.competitor_id} className="monitoring__comp-row">
										<CompName name={c.name} domain={c.domain} />
										<span className="ms-auto d-flex gap-1">
											<button
												type="button"
												className="monitoring__trend-toggle"
												onClick={() => track(c)}
											>
												Track
											</button>
											<button
												type="button"
												className="monitoring__trend-toggle monitoring__trend-toggle--danger"
												onClick={() => ignore(c.competitor_id)}
											>
												Ignore
											</button>
										</span>
									</li>
								))}
							</ul>
						</div>
					)}
					{ignored.length > 0 && (
						<div className="mt-3">
							<p className="monitoring__panel-caption mb-2">Ignored</p>
							<ul className="monitoring__comp-list list-unstyled mb-0">
								{ignored.map((c) => (
									<li key={c.competitor_id} className="monitoring__comp-row">
										<CompName name={c.name} domain={c.domain} />
										<button
											type="button"
											className="monitoring__trend-toggle ms-auto"
											onClick={() => track(c)}
										>
											Re-add
										</button>
									</li>
								))}
							</ul>
						</div>
					)}
				</article>

				<article className="monitoring__panel monitoring__panel--table">
					<h3 className="monitoring__panel-title">Top cited sources — latest run</h3>
					<p className="monitoring__panel-caption">
						Domains AI cited alongside you (share-of-citation).
					</p>
					{sources.length === 0 ? (
						<p className="monitoring__empty text-muted mb-0 px-3 pb-3">
							No external sources cited this run.
						</p>
					) : (
						<div className="monitoring__sources-table-wrap">
							<table className="monitoring__table monitoring__sources-table">
								<thead>
									<tr>
										<th scope="col">Domain</th>
										<th scope="col" className="monitoring__th-num">
											Prompts
										</th>
									</tr>
								</thead>
								<tbody>
									{sources.map((row) => (
										<tr key={row.domain}>
											<td className="monitoring__sources-domain">
												{row.domain}
											</td>
											<td className="monitoring__td-num">
												{row.citations30d}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</article>
			</div>
		</div>
	);
}
